import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Mail, MessageCircle, Search, ChevronDown, Apple, Smartphone,
  CreditCard, ShieldCheck, Trash2, User, HelpCircle, Book, Wifi, RefreshCw,
  Target, Bug, Download, LogIn, ExternalLink,
} from 'lucide-react';

// Contact info — surfaces prominently at the top of the page.
const CONTACT_EMAIL = 'supportclaypulse@gmail.com';
const RESPONSE_TIME = 'Réponse sous 24 à 48 h ouvrés';

// FAQ entries grouped by category. Keep answers short + actionable.
const FAQ_CATEGORIES = [
  {
    id: 'install',
    label: 'Installation & Démarrage',
    icon: Download,
    items: [
      {
        q: "Comment installer ClayPulse sur iPhone ?",
        a: "ClayPulse est disponible sur l'App Store. Rendez-vous sur https://apps.apple.com/fr/app/claypulse/id6762091911 ou recherchez « ClayPulse » dans l'App Store depuis votre iPhone, puis appuyez sur Obtenir pour installer. L'app fonctionne sur iOS 15 et supérieur.",
      },
      {
        q: "Comment installer ClayPulse sur Android ?",
        a: "Rendez-vous sur claypulsepro.com/android depuis votre téléphone Android, téléchargez le fichier APK (v1.0.39), puis autorisez l'installation depuis des sources inconnues dans les paramètres de sécurité de votre appareil. Une fois installé, ClayPulse se comporte comme une application classique.",
      },
      {
        q: "Combien coûte ClayPulse ?",
        a: "ClayPulse propose 7 jours d'essai gratuit sans carte bancaire. Après l'essai, l'abonnement est de 6,99 €/mois ou 67 €/an (soit 5,58 €/mois, économie de 20%). Un seul compte donne accès à toutes les fonctionnalités.",
      },
      {
        q: "L'app fonctionne-t-elle sans connexion internet ?",
        a: "Oui. La majorité des fonctionnalités (sessions, statistiques, arsenal, stock cartouches) fonctionnent hors ligne. Vos données se synchronisent automatiquement dès que vous récupérez du réseau.",
      },
    ],
  },
  {
    id: 'account',
    label: 'Compte & Connexion',
    icon: LogIn,
    items: [
      {
        q: "Je n'arrive pas à me connecter à mon compte",
        a: "Vérifiez que l'email et le mot de passe sont corrects (attention aux majuscules). Si vous avez oublié votre mot de passe, utilisez le lien 'Mot de passe oublié' sur l'écran de connexion — un email de réinitialisation vous sera envoyé sous quelques minutes. Vérifiez également vos spams.",
      },
      {
        q: "Je n'ai pas reçu l'email de confirmation ou de réinitialisation",
        a: "Vérifiez votre dossier spam / courriers indésirables. Si vous ne trouvez rien, contactez-nous à supportclaypulse@gmail.com en précisant l'email utilisé pour l'inscription et nous vous débloquerons manuellement.",
      },
      {
        q: "Puis-je utiliser mon compte sur plusieurs appareils ?",
        a: "Oui, votre abonnement fonctionne sur tous vos appareils (iPhone + iPad + Android). Connectez-vous avec le même email et vos données seront synchronisées automatiquement.",
      },
      {
        q: "Comment changer mon adresse email ou mon mot de passe ?",
        a: "Ouvrez l'app > Onglet Compte > Modifier mes informations. Pour toute autre modification (fusion de comptes, changement de nom d'utilisateur), contactez-nous par email.",
      },
    ],
  },
  {
    id: 'subscription',
    label: 'Abonnement & Facturation',
    icon: CreditCard,
    items: [
      {
        q: "Comment gérer ou annuler mon abonnement ?",
        a: "Ouvrez l'app > Compte > Gérer mon abonnement. Vous serez redirigé vers le portail Stripe où vous pouvez mettre à jour votre moyen de paiement, changer de formule ou annuler votre abonnement. L'annulation prend effet à la fin de la période en cours (vous gardez l'accès Pro jusque-là).",
      },
      {
        q: "J'ai payé mais je n'ai pas accès à ClayPulse Pro",
        a: "Cela peut arriver si votre paiement Stripe n'a pas encore été lié à votre appareil. Envoyez-nous un email à supportclaypulse@gmail.com avec l'adresse email utilisée pour le paiement — nous débloquerons votre accès Pro sous 24 h.",
      },
      {
        q: "Puis-je obtenir un remboursement ?",
        a: "Nous étudions chaque demande de remboursement au cas par cas. Contactez-nous à supportclaypulse@gmail.com en expliquant votre situation. En cas d'insatisfaction dans les 14 premiers jours de votre abonnement payant, un remboursement complet est généralement accordé.",
      },
      {
        q: "Puis-je passer du plan mensuel au plan annuel (ou inversement) ?",
        a: "Oui, depuis Compte > Gérer mon abonnement. Le pro-rata est calculé automatiquement par Stripe.",
      },
      {
        q: "Que se passe-t-il à la fin de l'essai gratuit ?",
        a: "Aucun paiement automatique. Vous devez choisir explicitement de vous abonner pour continuer à utiliser les fonctionnalités Pro. Vos données restent conservées si vous décidez de vous abonner plus tard.",
      },
    ],
  },
  {
    id: 'features',
    label: 'Fonctionnalités',
    icon: Target,
    items: [
      {
        q: "Quelles disciplines sont supportées par ClayPulse ?",
        a: "8 disciplines : Skeet Olympique, Skeet Universel, Trap Olympique, Trap ACO, DTL (Down The Line), Compak Sporting, Parcours de Chasse et Ball-Trap de Campagne. Chaque discipline a ses propres statistiques dédiées.",
      },
      {
        q: "Comment fonctionne Clayo, le coach IA ?",
        a: "Clayo est un assistant intelligent basé sur Google Gemini qui analyse vos sessions, votre matériel et votre historique pour vous donner des conseils personnalisés. Posez-lui une question dans l'app et il vous répond en quelques secondes.",
      },
      {
        q: "Puis-je exporter mes données de session ?",
        a: "Oui. Depuis Compte > Exporter mes données, vous pouvez télécharger l'ensemble de vos sessions, statistiques et informations personnelles au format JSON (RGPD compatible).",
      },
      {
        q: "Comment ajouter un événement Ball-Trap de Campagne sur la carte ?",
        a: "Depuis la page 'La carte du tireur sportif' du site, cliquez sur 'Soumettre un événement'. Le formulaire vous demandera nom, dates, lieu, disciplines, tarifs et une affiche optionnelle. Un modérateur validera votre soumission sous 48 h.",
      },
    ],
  },
  {
    id: 'troubleshoot',
    label: 'Dépannage',
    icon: Bug,
    items: [
      {
        q: "L'app plante ou ne s'ouvre plus",
        a: "1) Fermez complètement l'app puis rouvrez-la. 2) Vérifiez qu'elle est à jour (Android : téléchargez le dernier APK depuis /android). 3) Redémarrez votre téléphone. Si le problème persiste, envoyez-nous une capture d'écran de l'erreur à supportclaypulse@gmail.com avec le modèle de votre téléphone et la version d'OS.",
      },
      {
        q: "Mes données ne se synchronisent pas",
        a: "Vérifiez votre connexion internet. Ouvrez l'app en Wi-Fi ou 4G/5G, puis rendez-vous dans Compte > Forcer la synchronisation. Si aucune donnée n'apparaît sur un nouvel appareil, assurez-vous d'être bien connecté au même compte.",
      },
      {
        q: "Les statistiques ne s'affichent pas correctement",
        a: "Faites glisser vers le bas sur l'écran Statistiques pour forcer un rafraîchissement. Si le problème persiste, essayez de vous déconnecter puis de vous reconnecter — cela redemande les données au serveur.",
      },
      {
        q: "J'ai perdu mes données après avoir changé de téléphone",
        a: "Vos données sont liées à votre compte, pas à l'appareil. Reconnectez-vous simplement avec le même email et vos données réapparaîtront. Si ce n'est pas le cas, contactez-nous — nous pouvons migrer votre device_id manuellement.",
      },
    ],
  },
  {
    id: 'privacy',
    label: 'Confidentialité & Suppression',
    icon: ShieldCheck,
    items: [
      {
        q: "Quelles données ClayPulse collecte-t-il ?",
        a: "Uniquement les données nécessaires au fonctionnement de l'app : email, mot de passe chiffré, sessions de tir, arsenal, cartouches, et statistiques dérivées. Aucune donnée n'est vendue ni partagée avec des tiers publicitaires. Voir notre Politique de confidentialité pour le détail complet.",
      },
      {
        q: "Comment supprimer définitivement mon compte et mes données ?",
        a: "Depuis l'app : Compte > Paramètres > Supprimer mon compte. Cette action est irréversible : toutes vos données (sessions, statistiques, arsenal, abonnement) seront supprimées définitivement sous 48 h. Vous pouvez aussi envoyer une demande à supportclaypulse@gmail.com si vous ne pouvez plus accéder à l'app.",
      },
      {
        q: "Où sont hébergées mes données ?",
        a: "Nos serveurs et notre base de données sont hébergés en Europe (Union Européenne). Nous respectons le RGPD. Les paiements sont traités par Stripe (PCI-DSS conforme).",
      },
      {
        q: "Puis-je récupérer une copie de toutes mes données ?",
        a: "Oui, gratuitement et à tout moment via Compte > Exporter mes données. Vous recevez un fichier JSON contenant l'intégralité de vos informations (droit à la portabilité RGPD).",
      },
    ],
  },
];

export default function Support() {
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');

  // Client-side search across all FAQ entries.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_CATEGORIES;
    return FAQ_CATEGORIES
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
        <Link
          to="/"
          data-testid="support-back-home"
          className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#FF6B35] text-sm mb-10 transition-colors"
        >
          <ArrowLeft size={16} />
          Retour à l&apos;accueil
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-4">
            <HelpCircle size={13} className="text-[#FF6B35]" />
            <span className="text-[#FF6B35] text-[11px] font-semibold uppercase tracking-wider">
              Centre d&apos;assistance
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black font-['Poppins'] leading-tight mb-3">
            Comment pouvons-nous <span className="text-[#FF6B35]">t&apos;aider</span>&nbsp;?
          </h1>
          <p className="text-[#A0A0A0] text-base sm:text-lg max-w-2xl">
            Réponses aux questions les plus fréquentes sur ClayPulse — installation, abonnement,
            dépannage, données personnelles. Si tu ne trouves pas ta réponse, on te répond
            personnellement par email.
          </p>
        </motion.header>

        {/* Contact card */}
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          data-testid="support-contact-card"
          className="rounded-2xl bg-gradient-to-br from-[#FF6B35]/8 to-[#FF8A5B]/4 border border-[#FF6B35]/25 p-6 sm:p-7 mb-10"
        >
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-[#FF6B35] flex items-center justify-center shrink-0">
              <Mail size={22} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold font-['Poppins'] mb-1">Contact direct</h2>
              <p className="text-[#C8C8C8] text-sm mb-3">
                Envoyez-nous un email — l&apos;équipe ClayPulse répond en personne à chaque
                demande.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Demande%20d%27assistance%20ClayPulse`}
                data-testid="support-contact-email"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B35] text-white font-semibold text-sm hover:bg-[#F76B1C] hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] transition-all"
              >
                <Mail size={15} />
                {CONTACT_EMAIL}
              </a>
              <p className="text-[#888] text-xs mt-3 flex items-center gap-1.5">
                <RefreshCw size={11} />
                {RESPONSE_TIME}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Quick platform tiles */}
        <section className="grid sm:grid-cols-2 gap-3 mb-10" data-testid="support-platform-tiles">
          <div className="p-5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <Apple size={18} className="text-white" />
              <span className="text-white font-semibold text-sm">iOS (iPhone / iPad)</span>
            </div>
            <p className="text-[#A0A0A0] text-xs mb-3 leading-relaxed">
              Version officielle disponible sur l&apos;App Store. iOS 15 et supérieur.
              7 jours d&apos;essai gratuit.
            </p>
            <Link to="/ios" className="text-[#FF6B35] hover:underline text-xs font-semibold">
              Voir la page iOS →
            </Link>
          </div>
          <div className="p-5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone size={18} className="text-white" />
              <span className="text-white font-semibold text-sm">Android</span>
            </div>
            <p className="text-[#A0A0A0] text-xs mb-3 leading-relaxed">
              Version 1.0.39 disponible en téléchargement direct. Compatibilité :
              Android 8+ (Oreo et supérieur).
            </p>
            <Link to="/android" className="text-[#FF6B35] hover:underline text-xs font-semibold">
              Télécharger l&apos;APK →
            </Link>
          </div>
        </section>

        {/* Search bar */}
        <section className="mb-8">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une question, un mot-clé..."
              data-testid="support-search"
              className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder:text-[#666] focus:outline-none focus:border-[#FF6B35]/60 transition"
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="space-y-8 mb-14" data-testid="support-faq-section">
          {filtered.length === 0 && (
            <div className="text-center py-10 text-[#A0A0A0]">
              Aucun résultat pour «&nbsp;{query}&nbsp;». Essayez d&apos;autres mots-clés ou{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF6B35] hover:underline">
                contactez-nous
              </a>.
            </div>
          )}
          {filtered.map((cat) => {
            const Icon = cat.icon;
            return (
              <div key={cat.id}>
                <h3 className="flex items-center gap-2 text-lg font-bold font-['Poppins'] text-white mb-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#FF6B35]/10 border border-[#FF6B35]/30">
                    <Icon size={16} className="text-[#FF6B35]" />
                  </span>
                  {cat.label}
                </h3>
                <div className="space-y-2">
                  {cat.items.map((item, i) => {
                    const uid = `${cat.id}-${i}`;
                    const isOpen = openId === uid;
                    return (
                      <div
                        key={uid}
                        className={`rounded-xl border transition-colors ${
                          isOpen
                            ? 'bg-white/[0.04] border-[#FF6B35]/25'
                            : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                        }`}
                      >
                        <button
                          onClick={() => setOpenId(isOpen ? null : uid)}
                          data-testid={`faq-toggle-${uid}`}
                          className="w-full flex items-start justify-between gap-3 text-left px-5 py-4"
                        >
                          <span className="text-white text-sm sm:text-base font-medium leading-snug pt-0.5">
                            {item.q}
                          </span>
                          <ChevronDown
                            size={18}
                            className={`text-[#888] shrink-0 mt-1 transition-transform duration-300 ${
                              isOpen ? 'rotate-180 text-[#FF6B35]' : ''
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            <div className="px-5 pb-5 text-[#C8C8C8] text-sm sm:text-[15px] leading-relaxed whitespace-pre-wrap">
                              {item.a}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>

        {/* Guides step-by-step */}
        <section className="mb-14" data-testid="support-guides-section">
          <h2 className="text-2xl font-bold font-['Poppins'] mb-6 flex items-center gap-2">
            <Book size={20} className="text-[#FF6B35]" />
            Guides pas à pas
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {
                icon: Smartphone,
                title: "Installer ClayPulse sur Android",
                steps: [
                  "Ouvrir claypulsepro.com/android depuis votre téléphone",
                  "Télécharger le fichier APK v1.0.39",
                  "Autoriser l'installation depuis sources inconnues (Paramètres > Sécurité)",
                  "Ouvrir le fichier téléchargé pour installer",
                  "Se connecter avec votre compte ou en créer un nouveau",
                ],
              },
              {
                icon: LogIn,
                title: "Créer un compte et démarrer l'essai",
                steps: [
                  "Ouvrir l'app puis appuyer sur « Créer un compte »",
                  "Saisir votre email et un mot de passe",
                  "Valider — l'essai de 7 jours démarre automatiquement",
                  "Explorer toutes les fonctionnalités Pro sans engagement",
                  "À la fin de l'essai, choisir votre plan (mensuel ou annuel)",
                ],
              },
              {
                icon: CreditCard,
                title: "Gérer ou annuler l'abonnement",
                steps: [
                  "Ouvrir l'app > onglet Compte",
                  "Appuyer sur « Gérer mon abonnement »",
                  "Redirection vers le portail Stripe sécurisé",
                  "Modifier le moyen de paiement, changer de formule ou annuler",
                  "L'annulation prend effet à la fin de la période payée",
                ],
              },
              {
                icon: Trash2,
                title: "Supprimer mon compte définitivement",
                steps: [
                  "Ouvrir l'app > onglet Compte > Paramètres",
                  "Faire défiler vers le bas jusqu'à « Supprimer mon compte »",
                  "Confirmer la suppression (action irréversible)",
                  "Toutes vos données sont effacées sous 48 h",
                  "Alternative : envoyer une demande à supportclaypulse@gmail.com",
                ],
              },
            ].map((g, gi) => (
              <div
                key={gi}
                className="p-5 rounded-xl bg-white/[0.03] border border-white/5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <g.icon size={17} className="text-[#FF6B35]" />
                  <span className="text-white font-semibold text-sm">{g.title}</span>
                </div>
                <ol className="space-y-2 list-none">
                  {g.steps.map((s, si) => (
                    <li
                      key={si}
                      className="flex items-start gap-2.5 text-[#C8C8C8] text-xs sm:text-sm leading-relaxed"
                    >
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF6B35]/15 border border-[#FF6B35]/30 text-[#FF6B35] text-[10px] font-bold mt-0.5">
                        {si + 1}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* System requirements */}
        <section className="mb-14 rounded-2xl bg-white/[0.02] border border-white/5 p-6 sm:p-7" data-testid="support-requirements">
          <h2 className="text-xl font-bold font-['Poppins'] mb-4 flex items-center gap-2">
            <Wifi size={18} className="text-[#FF6B35]" />
            Compatibilité et prérequis
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 text-sm">
            <div>
              <p className="text-white font-semibold mb-2">iOS (iPhone / iPad)</p>
              <ul className="space-y-1.5 text-[#C8C8C8]">
                <li>· Version PWA : iOS 15+ recommandé (Safari)</li>
                <li>· Version App Store : sortie prochaine (iOS 15+)</li>
                <li>· ≈ 25 Mo d&apos;espace disque</li>
                <li>· Connexion internet pour la synchronisation</li>
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold mb-2">Android</p>
              <ul className="space-y-1.5 text-[#C8C8C8]">
                <li>· Android 8.0 (Oreo) ou supérieur</li>
                <li>· APK direct (v1.0.39)</li>
                <li>· ≈ 40 Mo d&apos;espace disque</li>
                <li>· Autoriser les sources inconnues à l&apos;installation</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Legal links */}
        <section className="mb-14 text-center" data-testid="support-legal-links">
          <h2 className="text-lg font-bold font-['Poppins'] mb-4">Documents légaux</h2>
          <div className="flex flex-wrap justify-center gap-2">
            <Link
              to="/privacy"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white text-xs font-semibold hover:border-[#FF6B35]/30 hover:bg-white/[0.05] transition"
            >
              <ShieldCheck size={13} /> Politique de confidentialité
            </Link>
            <Link
              to="/cgu"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white text-xs font-semibold hover:border-[#FF6B35]/30 hover:bg-white/[0.05] transition"
            >
              <User size={13} /> Conditions d&apos;utilisation
            </Link>
          </div>
        </section>

        {/* Footer help */}
        <section className="rounded-2xl bg-gradient-to-br from-[#1a1a1a] to-[#0D0D0D] border border-white/5 p-6 sm:p-8 text-center">
          <MessageCircle size={26} className="text-[#FF6B35] mx-auto mb-3" />
          <h3 className="text-white text-lg font-bold font-['Poppins'] mb-2">
            Toujours besoin d&apos;aide&nbsp;?
          </h3>
          <p className="text-[#A0A0A0] text-sm mb-5">
            Notre équipe répond à toutes les demandes personnellement, sous 24 à 48 h ouvrés.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Demande%20d%27assistance%20ClayPulse`}
            data-testid="support-footer-contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold text-sm hover:shadow-[0_0_25px_rgba(255,107,53,0.4)] transition-all hover:scale-105"
          >
            <ExternalLink size={15} />
            Nous contacter
          </a>
        </section>
      </div>
    </div>
  );
}
